import { useEffect } from 'react';
import {
  darkenColor,
  getContrastColor,
  getHoverContrastColor,
  getReadableOnLight,
  hexToRgbString,
  lightenColor,
} from '../utils/colorUtils';

const DEFAULT_PUBLIC_COLOR = '#0f3a60';

export function useOrgTheming(
  primaryColor: string | null | undefined,
  hostElement?: HTMLElement | null,
): void {
  useEffect(() => {
    const primary = primaryColor || DEFAULT_PUBLIC_COLOR;
    const primaryDark = darkenColor(primary, 20);
    const primaryLight = lightenColor(primary, 20);
    const secondary = darkenColor(primary, 30);
    const secondaryDark = darkenColor(secondary, 15);
    const secondaryLight = lightenColor(secondary, 15);
    // Foreground for text/icons painted on the primary colour. Derived so a
    // light or white org colour doesn't leave white-on-white buttons.
    const onPrimary = getContrastColor(primary);
    // Hover text for primary buttons: the opposite shade when it still reads, else the same.
    const onPrimaryHover = getHoverContrastColor(primary);
    // Same foreground as an "r, g, b" triplet so translucent header chrome can
    // invert with the header (white washes on dark, dark washes on light).
    const onPrimaryRgb = hexToRgbString(onPrimary) || '255, 255, 255';
    // Primary colour clamped to stay legible as a foreground accent on white
    // surfaces (CTAs, labels, focus rings). Unchanged for dark brand colours.
    const primaryReadable = getReadableOnLight(primary);
    // "r, g, b" triplet for the rgba(var(--primary-color-rgb), a) tints
    // (focus glows, hover/selected tints, the trade-in pill). Plain rgba()
    // after substitution, so it can't fail at computed-value time the way
    // color-mix(var()) did in Firefox. Falls back to the default colour's
    // triplet for an unparseable org colour.
    const primaryRgb = hexToRgbString(primary) || hexToRgbString(DEFAULT_PUBLIC_COLOR)!;

    const targets: HTMLElement[] = [document.documentElement];
    if (hostElement) targets.push(hostElement);

    for (const el of targets) {
      el.style.setProperty('--primary-color', primary);
      el.style.setProperty('--text-on-primary', onPrimary); // primary buttons stay readable on a pale org colour
      el.style.setProperty('--text-on-primary-hover', onPrimaryHover);
      el.style.setProperty('--primary-color-rgb', primaryRgb);
      el.style.setProperty('--primary-color-dark', primaryDark);
      el.style.setProperty('--primary-color-light', primaryLight);
      el.style.setProperty('--secondary-color', secondary);
      el.style.setProperty('--secondary-color-dark', secondaryDark);
      el.style.setProperty('--secondary-color-light', secondaryLight);
      el.style.setProperty('--agent-on-primary', onPrimary);
      el.style.setProperty('--agent-on-primary-rgb', onPrimaryRgb);
      el.style.setProperty('--agent-primary-readable', primaryReadable);
    }
  }, [primaryColor, hostElement]);
}
